import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import { createSuggestionStorePlugin, aiSuggestionPluginKey } from './suggestion-store';
import { createAiCaretPlugin, aiCaretPluginKey } from './ai-caret';
import type { ReviewMode } from '@/lib/ai-composer/types';
import styles from './ai-editing.module.css';

export interface BeginArgs { id: string; from: number; to: number; text: string; mode: ReviewMode; streaming?: boolean; }
export interface UpdateArgs { id: string; text: string; streaming?: boolean; from?: number; to?: number; before?: string; }
export interface CaretArgs { pos: number; state: 'thinking' | 'writing'; label: string; }

export interface SuggestionInfo { id: string; from: number; to: number; mode: ReviewMode; }

export interface AiEditingApi {
  begin(args: BeginArgs): void;
  update(args: UpdateArgs): void;
  clear(id: string): void;
  setCaret(args: CaretArgs): void;
  clearCaret(): void;
  getSuggestions(): SuggestionInfo[];
  aiAccept(id: string): void;
  aiReject(id: string): void;
  aiAcceptAll(): void;
  aiRejectAll(): void;
}

export const AiEditing = Extension.create({
  name: 'aiEditing',
  addProseMirrorPlugins() {
    return [
      createSuggestionStorePlugin(
        styles.ghost,
        styles.ghostStreaming,
        styles.previewAdded,
        styles.reviewAdded,
        styles.reviewRemoved,
      ),
      createAiCaretPlugin(styles.caret, styles.caretThinking, styles.caretWriting),
    ];
  },
});

export function getAiEditing(editor: Editor): AiEditingApi {
  const dispatch = (key: typeof aiSuggestionPluginKey | typeof aiCaretPluginKey, meta: unknown) => {
    if (editor.isDestroyed || !editor.state) return;
    editor.view.dispatch(editor.state.tr.setMeta(key, meta));
  };

  function getSuggestions(): SuggestionInfo[] {
    if (editor.isDestroyed || !editor.state) return [];
    const set = aiSuggestionPluginKey.getState(editor.state);
    if (!set) return [];
    return set
      .find()
      .filter((d) => (d.spec as { key?: string }).key?.endsWith('-range'))
      .map((d) => {
        const spec = d.spec as { key: string; mode?: ReviewMode };
        const id = /^ai-(.+)-range$/.exec(spec.key)?.[1] ?? spec.key;
        return { id, from: d.from, to: d.to, mode: spec.mode ?? 'ghost-write' };
      });
  }

  return {
    begin: (a) => dispatch(aiSuggestionPluginKey, { type: 'set', ...a }),
    update: (a) => dispatch(aiSuggestionPluginKey, { type: 'update', ...a }),
    clear: (id) => dispatch(aiSuggestionPluginKey, { type: 'clear', id }),
    setCaret: (a) => dispatch(aiCaretPluginKey, { type: 'setCaret', ...a }),
    // No-op when there's no caret to clear — a per-render idle caller would otherwise
    // dispatch an empty-clear transaction every render, churning the editor for nothing.
    clearCaret: () => {
      if (editor.isDestroyed || !editor.state) return;
      const set = aiCaretPluginKey.getState(editor.state);
      if (!set || set.find().length === 0) return;
      dispatch(aiCaretPluginKey, { type: 'clearCaret' });
    },
    getSuggestions,
    // DECORATION-ONLY cleanup: removes the suggestion decoration from the editor.
    // The caller is responsible for invoking the applier's accept/reject to commit
    // or revert the doc mutation — these methods do not touch the document content.
    aiAccept: (id) => dispatch(aiSuggestionPluginKey, { type: 'clear', id }),
    aiReject: (id) => dispatch(aiSuggestionPluginKey, { type: 'clear', id }),
    aiAcceptAll: () => { for (const s of getSuggestions()) dispatch(aiSuggestionPluginKey, { type: 'clear', id: s.id }); },
    aiRejectAll: () => { for (const s of getSuggestions()) dispatch(aiSuggestionPluginKey, { type: 'clear', id: s.id }); },
  };
}
